// FLAG:useDocuAi — display named notices for “known benign situations” in the review.
//
// At the end of analysis, DocuAI recognizes named situations in which the structured result
// legitimately differs from a naive expectation and attaches them to the contract
// (`common.notices`; detection in ai/backend/app/services/notices.py, plan
// docs/known-situations-registry-plan.md). contractToAnalysis stores them as
// `invoiceFacts.notices` — the same path used by vatJurisdiction and lineTaxAssumed.
//
// Why this needs a display at all: the triggering receipt (zuferd_656211153.pdf, ZUGFeRD 1.0
// BASIC with an empty position row) was reported
// TWICE — issues 36761b0a and 04c0203b — because it
// was correctly identified as “having no line items” and nobody was told. To the customer, a
// recognized but invisible situation is indistinguishable from a defect.
//
// This file knows NO notice code. Title and text arrive fully resolved from the contract (1.8.0)
// against the DocuAI catalog; this code only collects, deduplicates, and forwards them. That is
// deliberate and central to the split: whoever owns the wording owns the meaning — keeping the
// text here would describe the same situation in
// two repos and let the versions diverge. This way,
// an operator adds a situation to the catalog and it appears without an orgaMAX Web release.
//
// An envelope from BEFORE 1.8.0 carries only the code. In that case `message` is absent and the
// entry is silently discarded: no notice is better than a raw identifier that tells the reviewer
// nothing. Such receipts receive their notice with the next analysis.
//
// Important difference: a notice is NOT a problem. KoSIT/`validationProblem` says “the receipt
// is wrong” and is rendered in red; a notice says “this is entirely normal, and here is why” and
// remains neutral.
//
// `enabled` is the STRUCTURAL flag gate, as in unbookableVatFact: callers pass useDocuAi through;
// with the flag OFF the function returns [] and the display does not exist. Deliberately not a
// data-based argument (“flag OFF means no invoiceFacts here”) — that is true today, but it is a
// claim about every writer of the file metadata and not something the flag guarantee should rely
// on.

export interface AnalysisNotice {
  code?: string | null
  severity?: string | null
  scope?: string | null
  title?: string | null
  message?: string | null
}

export interface DisplayableAnalysisNotice {
  code: string
  title: string | null
  message: string
}

interface NoticeFacts {
  notices?: AnalysisNotice[] | null
}

interface FactCarrier {
  invoiceFacts?: NoticeFacts | null
}

interface RelatedFile {
  documentInvoiceDetails?: FactCarrier | null
  metaData?: (FactCarrier & { ocr?: FactCarrier | null }) | null
}

interface FactSources {
  /** metaData.invoiceFacts of the displayed receipt (populated by the review component). */
  invoiceFacts?: NoticeFacts | null
  /** The reviewed record that the expense/revenue host loads anyway. */
  reviewDetails?: FactCarrier | null
  /** The receipt's filesToAssign + receipts. */
  relatedFiles?: RelatedFile[] | null
}

/**
 * All sources where the facts may reside depending on the entry point — identical to
 * unbookableVatFact so a receipt says the same thing in the review component and the expense/revenue form.
 */
const collectNotices = ({ invoiceFacts, reviewDetails, relatedFiles }: FactSources): AnalysisNotice[] => [
  invoiceFacts,
  reviewDetails?.invoiceFacts,
  ...(relatedFiles || []).flatMap(file => [
    file?.documentInvoiceDetails?.invoiceFacts,
    file?.metaData?.invoiceFacts,
    file?.metaData?.ocr?.invoiceFacts,
  ]),
].flatMap(facts => facts?.notices || [])

const text = (value: unknown): string | null =>
  (typeof value === 'string' && value.trim() !== '' ? value : null)

/**
 * @param enabled useDocuAi — with the flag OFF, the result is always empty.
 * @returns Displayable notices in the order reported by the analysis, deduplicated by code (the
 *   same receipt arrives through multiple sources) and excluding entries without accompanying
 *   text.
 */
export const findAnalysisNotices = (
  enabled: boolean,
  sources: FactSources = {},
): DisplayableAnalysisNotice[] => {
  if (!enabled) return []

  const seen = new Set<string>()
  const displayable: DisplayableAnalysisNotice[] = []

  collectNotices(sources).forEach(notice => {
    const code = text(notice?.code)
    const message = text(notice?.message)
    if (!code || !message || seen.has(code)) return
    seen.add(code)
    displayable.push({ code, title: text(notice?.title), message })
  })

  return displayable
}
